/**
 * Post-settle summary modal shown to the driver after they "Rozlicz zlecenie".
 *
 * Shows a quick recap so the courier can verify:
 *   • how many packages were delivered
 *   • how many were undelivered (awizo/zwrot/powtórka) with a compact list
 *   • how the payout was computed (full × rate + multipack × 0,50)
 *   • the total COD (pobrania) they must deposit to the office
 */
import { X, PackageCheck, PackageX, Wallet, Mail, RotateCcw, Repeat, HelpCircle, Banknote, Trophy } from "lucide-react";
import { DayReportCard } from "./tracking/DayReportCard";

const REASON_META = {
  awizo:      { icon: Mail,        label: "Awizo",              color: "bg-amber-100 text-amber-800" },
  return:     { icon: RotateCcw,   label: "Zwrot do nadawcy",   color: "bg-rose-100 text-rose-800" },
  redelivery: { icon: Repeat,      label: "Powtórne doręczenie", color: "bg-violet-100 text-violet-800" },
  unknown:    { icon: HelpCircle,  label: "Bez powodu",         color: "bg-zinc-100 text-zinc-800" },
};

export default function SettleSummaryModal({ open, result, planName, onClose }) {
  if (!open || !result) return null;
  const {
    delivered = 0,
    awizo = 0,
    multipack_extra = 0,
    multipack_full = 0,
    rate = 0,
    multipack_extra_rate = 0.5,
    wallet_gain = 0,
    cod_collected = 0,
    cod_missed = 0,
    skipped_packages = [],
  } = result;
  const full = multipack_full || Math.max(delivered - multipack_extra, 0);

  return (
    <div
      onClick={onClose}
      data-testid="settle-summary-modal"
      className="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-3 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl my-6"
      >
        {/* Header */}
        <div className="p-5 bg-gradient-to-br from-emerald-500 via-green-600 to-emerald-800 text-white relative overflow-hidden">
          <Trophy className="absolute -right-6 -top-4 w-32 h-32 opacity-15" strokeWidth={1.5} />
          <div className="relative">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">
              // Zlecenie rozliczone
            </div>
            <h2 className="font-heading font-black uppercase tracking-tight text-2xl md:text-3xl mt-1">
              {planName || "Podsumowanie"}
            </h2>
            <p className="text-sm mt-1 text-white/85">
              Sprawdź czy wszystko się zgadza. Pobrania oddaj w biurze.
            </p>
          </div>
        </div>

        {/* Payout breakdown */}
        <div className="p-5 border-b border-zinc-100 space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            // Rozliczenie pieniędzy
          </div>
          <div
            data-testid="settle-payout-breakdown"
            className="flex flex-wrap items-center gap-2 text-sm font-mono"
          >
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200">
              <PackageCheck className="w-4 h-4" />
              {full} × {rate.toFixed(2)} zł
              <span className="text-emerald-600">=</span>
              <b>{(full * rate).toFixed(2)} zł</b>
            </span>
            {multipack_extra > 0 && (
              <>
                <span className="text-zinc-400">+</span>
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-50 text-violet-800 ring-1 ring-violet-200 font-bold uppercase text-xs tracking-wider"
                  title="Multipack — paczki dodatkowe pod tym samym adresem"
                >
                  Multipack {multipack_extra} × {multipack_extra_rate.toFixed(2)} zł
                  <span className="text-violet-500 normal-case">=</span>
                  {(multipack_extra * multipack_extra_rate).toFixed(2)} zł
                </span>
              </>
            )}
          </div>
          <div className="flex items-center justify-between p-3 rounded-xl bg-zinc-900 text-white">
            <div className="inline-flex items-center gap-2 text-sm font-heading font-black uppercase tracking-wider">
              <Wallet className="w-4 h-4 text-emerald-400" />
              Do portfela
            </div>
            <div className="text-2xl font-heading font-black text-emerald-400 tabular-nums" data-testid="settle-wallet-gain">
              +{wallet_gain.toFixed(2)} zł
            </div>
          </div>
        </div>

        {/* Package counts */}
        <div className="p-5 border-b border-zinc-100">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
            // Paczki
          </div>
          <div className="grid grid-cols-2 gap-2">
            <StatTile
              icon={PackageCheck} label="Doręczone" tone="emerald"
              value={delivered} testId="settle-delivered-count"
            />
            <StatTile
              icon={PackageX} label="Niedoręczone" tone="amber"
              value={awizo} testId="settle-awizo-count"
            />
          </div>
        </div>

        {/* Skipped packages list */}
        {skipped_packages.length > 0 && (
          <div className="p-5 border-b border-zinc-100 max-h-72 overflow-y-auto" data-testid="settle-skipped-list">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              // Niedoręczone paczki ({skipped_packages.length})
            </div>
            <div className="space-y-1.5">
              {skipped_packages.map((s) => {
                const meta = REASON_META[s.reason] || REASON_META.unknown;
                const Icon = meta.icon;
                const addrParts = [s.address_text, [s.zip, s.city].filter(Boolean).join(" ")].filter(Boolean);
                return (
                  <div
                    key={s.final_order}
                    data-testid={`settle-skipped-${s.final_order}`}
                    className="flex items-start gap-2 p-2 rounded-lg bg-zinc-50 ring-1 ring-zinc-200"
                  >
                    <div className="w-7 h-7 rounded-md bg-white ring-1 ring-zinc-300 flex items-center justify-center text-[10px] font-heading font-black text-zinc-600 shrink-0">
                      {s.final_order}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-zinc-900 truncate">
                        {s.client_name || `Paczka #${s.final_order}`}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 truncate">
                        {addrParts.join(", ") || "—"}
                        {s.parcel_number && <> · #{s.parcel_number}</>}
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${meta.color}`}
                    >
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* COD to deposit */}
        {(cod_collected > 0 || cod_missed > 0) && (
          <div className="p-5 border-b border-zinc-100">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
              // Pobrania (COD)
            </div>
            <div
              className="flex items-center justify-between p-3 rounded-xl bg-indigo-50 ring-1 ring-indigo-200"
              data-testid="settle-cod-block"
            >
              <div className="inline-flex items-center gap-2 text-sm font-heading font-black uppercase tracking-wider text-indigo-900">
                <Banknote className="w-4 h-4 text-indigo-700" />
                Do wpłaty w biurze
              </div>
              <div className="text-2xl font-heading font-black text-indigo-900 tabular-nums" data-testid="settle-cod-collected">
                {cod_collected.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł
              </div>
            </div>
            {cod_missed > 0 && (
              <div className="mt-2 text-[11px] font-mono text-amber-700">
                Pominięte pobrania (niedoręczone): {cod_missed.toLocaleString("pl-PL", { minimumFractionDigits: 2 })} zł — pieniądze zostają u klienta.
              </div>
            )}
          </div>
        )}

        {/* Day report — GPS km, work time, pace, idle stops */}
        <div className="p-5 border-b border-zinc-100 bg-zinc-50" data-testid="settle-day-report">
          <DayReportCard title="Twój dzień w liczbach" />
        </div>

        {/* Close */}
        <div className="p-4 flex gap-2">
          <button
            onClick={onClose}
            data-testid="settle-summary-close"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-zinc-900 hover:bg-zinc-700 text-white text-sm font-heading font-black uppercase tracking-wider"
          >
            <X className="w-4 h-4" /> Zamknij podsumowanie
          </button>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone, testId }) {
  const tones = {
    emerald: "from-emerald-500 to-green-700",
    amber:   "from-amber-500 to-orange-600",
  };
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden p-3 rounded-xl text-white bg-gradient-to-br ${tones[tone]}`}
    >
      <Icon className="absolute -right-2 -bottom-3 w-14 h-14 opacity-15" strokeWidth={1.5} />
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-3xl font-heading font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}
